import posthog from "posthog-js";
import type { AnalyticsConfig } from "./index.ts";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Initialise analytics. Call once per app in `instrumentation-client.ts`.
 * Sets PostHog's `app` super property so all events are tagged by app.
 */
export function initAnalytics({ appName }: AnalyticsConfig): void {
  if (typeof window === "undefined") {
    return;
  }

  const token = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST;

  if (!token) {
    return;
  }

  posthog.init(token, {
    api_host: host,
    defaults: "2026-01-30",
    // Session recording is disabled globally. The app handles sensitive recovery
    // data — recording must be explicitly re-enabled per route once a session
    // recording consent category and route exclusion strategy are in place.
    // See docs/issues/privacy-policy-gaps.md for context.
    disable_session_recording: true,
    // Do not set cookies until the user explicitly accepts analytics cookies.
    // On reject, PostHog falls back to cookieless (anonymous) mode.
    cookieless_mode: "on_reject",
  });

  posthog.register({ app: appName });
}

/**
 * Identify the authenticated user. Call after sign-in with the Supabase user ID.
 * Links all prior anonymous events to the identified user in PostHog.
 * Also sets the GA4 user_id if gtag is available.
 */
export function identifyUser(userId: string): void {
  posthog.identify(userId);

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("set", { user_id: userId });
  }
}

/**
 * Track a custom event in PostHog and GA4.
 */
export function trackEvent(
  name: string,
  properties?: Record<string, unknown>
): void {
  posthog.capture(name, properties);

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", name, properties);
  }
}

/**
 * Reset the identified user. Call on sign-out.
 */
export function resetUser(): void {
  posthog.reset();

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("set", { user_id: undefined });
  }
}

/**
 * Grant analytics consent. Call when the user accepts analytics cookies.
 * Opts PostHog into cookie-based tracking and updates GA4 Consent Mode.
 */
export function grantAnalyticsConsent(): void {
  posthog.opt_in_capturing();

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", { analytics_storage: "granted" });
  }
}

/**
 * Revoke analytics consent. Call when the user declines analytics cookies.
 * PostHog falls back to cookieless mode; GA4 stays denied.
 */
export function revokeAnalyticsConsent(): void {
  posthog.opt_out_capturing();

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", { analytics_storage: "denied" });
  }
}
